import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';

import { DSIcon } from '../../../base';
import ModalHeader from '../ModalHeader';
import Chip from '../Chip';
import BaseChip from '../BaseChip';

import LazyPhoto from './LazyPhoto';
import styles from './FullScreenPhotoList.module.scss';

const VIEWPORT_TRIGGER_OFFSET_PX = 80;

const slugify = str =>
  str
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

const groupPhotosByCaption = photos => {
  const sectionsMap = new Map();
  photos.forEach(photo => {
    const caption = photo.caption ?? 'Other';
    if (!sectionsMap.has(caption)) {
      sectionsMap.set(caption, { caption, photos: [] });
    }
    sectionsMap.get(caption).photos.push(photo);
  });
  return Array.from(sectionsMap.values());
};

const FullScreenPhotoListContent = ({
  onClose,
  onImageClick,
  headerActions = [],
  photos
}) => {
  const sections = useMemo(() => groupPhotosByCaption(photos), [photos]);
  const [activeSection, setActiveSection] = useState(
    sections[0]?.caption ?? null
  );
  const contentRef = useRef(null);
  const sectionRefsRef = useRef([]);

  const scrollToSection = useCallback(caption => {
    const id = `section-${slugify(caption)}`;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || sections.length === 0) {
      return undefined;
    }

    const handleScroll = () => {
      const contentRect = content.getBoundingClientRect();
      const triggerY = contentRect.top + VIEWPORT_TRIGGER_OFFSET_PX;
      sections.forEach((section, i) => {
        const el = sectionRefsRef.current[i];
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= triggerY && triggerY < rect.bottom) {
          setActiveSection(section.caption);
        }
      });
    };

    content.addEventListener('scroll', handleScroll, { passive: true });
    // Run after layout so section refs are populated; also support scroll on modal root if it's the actual scroll container

    const modalRoot =
      content.closest('[role="dialog"]') || content.parentElement;
    if (modalRoot && modalRoot !== content) {
      modalRoot.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      content.removeEventListener('scroll', handleScroll);
      if (modalRoot && modalRoot !== content) {
        modalRoot.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <Fragment>
      <div className={styles['full-screen-photo-list__top-bar']}>
        <ModalHeader
          backButtonClassName={styles['full-screen-photo-list__back-button']}
          label={trans('listing.fullScreenPhotoList.back')}
          onRequestClose={onClose}
          showBack
          showClose={false}
          className={styles['full-screen-photo-list__header']}
          titleClassName={styles['full-screen-photo-list__back-title']}
        />
        {headerActions && headerActions.length > 0 && (
          <div className={styles['full-screen-photo-list__header-actions']}>
            {headerActions.map((action, i) => (
              <Chip
                key={action.label ?? i}
                label={action.label}
                onClick={action.onClick}
                colorSchema={BaseChip.SCHEMAS.interactive}
                withBorder
                renderLeadingIcon={
                  action.iconName
                    ? () => <DSIcon name={action.iconName} size="sm" />
                    : null
                }
              />
            ))}
          </div>
        )}
      </div>

      <div className={styles['full-screen-photo-list__body']}>
        <nav className={styles['full-screen-photo-list__sidebar']}>
          <ul className={styles['full-screen-photo-list__nav-list']}>
            {sections.map(({ caption }) => (
              <li
                key={caption}
                className={styles['full-screen-photo-list__nav-item']}
              >
                <button
                  type="button"
                  className={classNames(
                    styles['full-screen-photo-list__nav-button'],
                    {
                      [styles['full-screen-photo-list__nav-button--active']]:
                        activeSection === caption
                    }
                  )}
                  onClick={() => scrollToSection(caption)}
                >
                  {caption}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div
          ref={contentRef}
          className={styles['full-screen-photo-list__content']}
        >
          {sections.map(({ caption, photos: sectionPhotos }, index) => (
            <section
              key={caption}
              ref={el => {
                sectionRefsRef.current[index] = el;
              }}
              id={`section-${slugify(caption)}`}
              className={styles['full-screen-photo-list__section']}
            >
              {sectionPhotos.map((photo, photoIndex) => (
                <LazyPhoto
                  key={photo.id}
                  photo={photo}
                  onImageClick={onImageClick}
                  className={classNames(
                    styles['full-screen-photo-list__image-wrapper'],
                    {
                      [styles['full-screen-photo-list__image-wrapper--first']]:
                        photoIndex === 0
                    }
                  )}
                  imageClassName={styles['full-screen-photo-list__image']}
                />
              ))}
            </section>
          ))}
        </div>
      </div>
    </Fragment>
  );
};

FullScreenPhotoListContent.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      caption: PropTypes.string.isRequired,
      verified: PropTypes.bool
    })
  ).isRequired,
  headerActions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired
    })
  ),
  onClose: PropTypes.func.isRequired,
  onImageClick: PropTypes.func.isRequired
};

export default FullScreenPhotoListContent;
